/**
 * Resumo e progresso de uma execução de checklist — regras puras, sem React.
 *
 * Tudo aqui é DERIVADO das respostas que já existem (nenhum campo novo na API):
 *
 * - **Atenção × Não conforme** não é uma resposta nova. O item continua
 *   conforme / não conforme / N/A (o formulário versionado não muda); quando o
 *   item vira ocorrência, a criticidade efetiva separa "atenção" (baixa/média)
 *   de "não conforme" (alta).
 * - **Pendente** é o item sem resposta com conteúdo, obrigatório ou não.
 */
import type {
  MaintenanceAnswerDto,
  MaintenanceDamageMapDto,
  MaintenanceItemDto,
  MaintenancePreviousCountDto,
  MaintenanceVersionDto,
} from "@bi/types";

export type EstadoItem = "pendente" | "conforme" | "atencao" | "nao_conforme" | "na";
export type EstadoSecao = "nao_iniciada" | "em_andamento" | "completa" | "com_problema";

/** Um item respondido é um item com conteúdo — foto conta como resposta no tipo `photo`. */
export function respondido(a: MaintenanceAnswerDto | undefined, item: MaintenanceItemDto, fotos = 0): boolean {
  // Tipo foto: respondido = tem foto (enviada ou na fila), como o servidor conta.
  if (item.answerType === "photo") return fotos > 0;
  if (!a) return false;
  // `!= null`: a resposta pendente na fila local pode não trazer todos os campos.
  // Mapa vazio é resposta ("sem avarias"); só a ausência do mapa não é.
  if (item.answerType === "damage_map") return a.jsonValue != null;
  return a.optionValue != null || a.numberValue != null || a.textValue != null;
}

/**
 * O item vai virar ocorrência ao concluir? Mesmas regras do servidor
 * (`triggersNonConformity` + queda de contagem) — a tela avisa antes.
 */
export function avaliarItem(item: MaintenanceItemDto, resposta: MaintenanceAnswerDto | undefined, anterior: MaintenancePreviousCountDto | undefined) {
  const quantidade = resposta?.numberValue != null ? Number(resposta.numberValue) : null;
  const abaixoDoMinimo =
    item.nonConformityTrigger === "below_minimum" && item.minQuantity != null && quantidade != null && quantidade < item.minQuantity;
  // Contou menos do que da última vez: some do conjunto sem ninguém marcar nada.
  const faltando = item.answerType === "quantity" && anterior && quantidade != null && quantidade < anterior.value ? anterior.value - quantidade : null;
  const mapa = (resposta?.jsonValue ?? null) as MaintenanceDamageMapDto | null;
  const naoConforme =
    (item.nonConformityTrigger === "non_conforming" && resposta?.optionValue === "nao_conforme") ||
    (item.nonConformityTrigger === "negative" && resposta?.optionValue === "nao") ||
    (item.nonConformityTrigger === "flagged_option" &&
      (item.options ?? []).some((o) => o.nonConforming && o.value === resposta?.optionValue)) ||
    (item.nonConformityTrigger === "damage_marked" && (mapa?.marks.length ?? 0) > 0) ||
    abaixoDoMinimo ||
    faltando != null;
  return { naoConforme, abaixoDoMinimo, faltando, mapa };
}

export function estadoDoItem(
  item: MaintenanceItemDto,
  resposta: MaintenanceAnswerDto | undefined,
  anterior: MaintenancePreviousCountDto | undefined,
  fotos = 0,
): EstadoItem {
  if (!respondido(resposta, item, fotos)) return "pendente";
  if (avaliarItem(item, resposta, anterior).naoConforme) {
    // Criticidade efetiva: a do item é o piso; o campo só pode elevar.
    const crit = resposta?.criticality ?? item.criticality;
    return crit === "high" ? "nao_conforme" : "atencao";
  }
  return resposta?.optionValue === "nao_se_aplica" ? "na" : "conforme";
}

export interface SecaoResumo {
  id: string;
  title: string;
  total: number;
  feitos: number;
  atencao: number;
  naoConformes: number;
  estado: EstadoSecao;
}

export interface ItemPendente {
  secaoIndex: number;
  secaoTitle: string;
  itemId: string;
  itemLabel: string;
}

export interface ResumoExecucao {
  total: number;
  feitos: number;
  conformes: number;
  atencao: number;
  naoConformes: number;
  na: number;
  pendentes: number;
  /** 0–100, sobre TODOS os itens (a conclusão em si só exige os obrigatórios) */
  progresso: number;
  observacoes: number;
  fotos: number;
  /** itens que vão gerar (ou geraram) não conformidade */
  ocorrencias: number;
  secoes: SecaoResumo[];
  obrigatoriosPendentes: ItemPendente[];
}

export function resumirExecucao(
  version: Pick<MaintenanceVersionDto, "sections">,
  respostas: Map<string, MaintenanceAnswerDto>,
  anteriores: Record<string, MaintenancePreviousCountDto>,
  fotosDoItem: (itemId: string) => number,
): ResumoExecucao {
  const r: ResumoExecucao = {
    total: 0, feitos: 0, conformes: 0, atencao: 0, naoConformes: 0, na: 0, pendentes: 0,
    progresso: 0, observacoes: 0, fotos: 0, ocorrencias: 0, secoes: [], obrigatoriosPendentes: [],
  };

  version.sections.forEach((s, secaoIndex) => {
    const sec: SecaoResumo = { id: s.id, title: s.title, total: s.items.length, feitos: 0, atencao: 0, naoConformes: 0, estado: "nao_iniciada" };
    for (const item of s.items) {
      const resp = respostas.get(item.id);
      const fotos = fotosDoItem(item.id);
      const estado = estadoDoItem(item, resp, anteriores[item.code], fotos);
      r.total++;
      r.fotos += fotos;
      if (resp?.observation) r.observacoes++;
      if (estado === "pendente") {
        r.pendentes++;
        if (item.required) r.obrigatoriosPendentes.push({ secaoIndex, secaoTitle: s.title, itemId: item.id, itemLabel: item.label });
        continue;
      }
      sec.feitos++;
      r.feitos++;
      if (estado === "conforme") r.conformes++;
      else if (estado === "na") r.na++;
      else if (estado === "atencao") { r.atencao++; sec.atencao++; r.ocorrencias++; }
      else { r.naoConformes++; sec.naoConformes++; r.ocorrencias++; }
    }
    sec.estado =
      sec.atencao + sec.naoConformes > 0 ? "com_problema"
        : sec.total > 0 && sec.feitos === sec.total ? "completa"
          : sec.feitos > 0 ? "em_andamento"
            : "nao_iniciada";
    r.secoes.push(sec);
  });

  r.progresso = r.total ? Math.round((r.feitos / r.total) * 100) : 100;
  return r;
}
